import React from 'react';

import { getBandColor, getRiskBand } from './bands';
import { LIKELIHOOD_LEVELS, SEVERITY_LEVELS } from './constants';
import { GridLayout, HorizontalAxis, MatrixCell, MatrixGrid, VerticalAxis } from './styles';
import { RiskMatrixGridProps } from './types';

// Likelihood up the rows, severity across. Stated once here so no surface decides it again.
const RiskMatrixGrid = ({ value, onSelect, onClear, cellSize, testId }: RiskMatrixGridProps): React.ReactElement => {
  const interactive = Boolean(onSelect);
  // Highest likelihood first, so the worst outcome sits at the top of the grid.
  const likelihoods = Array.from({ length: LIKELIHOOD_LEVELS }, (_, index) => LIKELIHOOD_LEVELS - index);
  const severities = Array.from({ length: SEVERITY_LEVELS }, (_, index) => index + 1);

  return (
    <GridLayout data-testid={testId}>
      {/* The arrow reads upward once the caption is rotated, matching "Severity" below. */}
      <VerticalAxis>Likelihood →</VerticalAxis>
      <div>
        <MatrixGrid $columns={SEVERITY_LEVELS} $cellSize={cellSize}>
          {likelihoods.map((likelihood) => (
            <React.Fragment key={likelihood}>
              {severities.map((severity) => {
                const score = likelihood * severity;
                const band = getRiskBand(severity, likelihood, SEVERITY_LEVELS, LIKELIHOOD_LEVELS);
                const { bg, color } = getBandColor(band);
                const selected = value?.likelihood === likelihood && value?.severity === severity;

                return (
                  <MatrixCell
                    key={`${likelihood}-${severity}`}
                    as={interactive ? 'button' : 'div'}
                    type={interactive ? 'button' : undefined}
                    $bg={bg}
                    $color={color}
                    $selected={selected}
                    $interactive={interactive}
                    $size={cellSize}
                    aria-pressed={interactive ? selected : undefined}
                    aria-label={`Likelihood ${likelihood}, severity ${severity}, score ${score}, ${band}`}
                    data-testid={`risk-matrix-cell-${likelihood}-${severity}`}
                    onClick={
                      interactive
                        ? (): void => {
                            // Re-picking the rated cell unrates it: a risk is logged before it is
                            // scored, so a mis-click has to be reversible.
                            if (selected && onClear) {
                              onClear();
                              return;
                            }
                            onSelect?.(likelihood, severity);
                          }
                        : undefined
                    }
                  >
                    {score}
                  </MatrixCell>
                );
              })}
            </React.Fragment>
          ))}
        </MatrixGrid>
        <HorizontalAxis>Severity →</HorizontalAxis>
      </div>
    </GridLayout>
  );
};

export default RiskMatrixGrid;
